import { useCallback, useMemo } from "react";
import {
  To,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { PROVIDER_PATHS } from "@/constants/paths";
import {
  APPOINTMENT_SCOPE,
  SCOPE_QUERY_PARAM,
} from "../group-appointment-constant";
import { AppointmentScope } from "../group-appointment-types";

export type UseAppointmentScope = {
  scope: AppointmentScope;
  setScope: (scope: AppointmentScope) => void;
  /** Like setScope, but off the appointments screens it opens that scope's list. */
  showScope: (scope: AppointmentScope) => void;
  /** Calendar or list route that keeps the current scope. */
  getViewPath: (view: "calendar" | "list") => To;
};

const SCOPE_PATH: Record<AppointmentScope, string> = {
  [APPOINTMENT_SCOPE.INDIVIDUAL]: PROVIDER_PATHS.APPOINTMENTS,
  [APPOINTMENT_SCOPE.GROUP]: PROVIDER_PATHS.GROUP_APPOINTMENTS,
};

/**
 * Individual / Group toggle on the appointments screen. The list view carries
 * the scope in its route; the calendar has no group route of its own, so there
 * it rides in the query string. Either way it survives a view switch and a
 * refresh.
 */
export const useAppointmentScope = (): UseAppointmentScope => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isListView = pathname.startsWith(PROVIDER_PATHS.APPOINTMENTS);
  const isCalendarView = pathname === PROVIDER_PATHS.CALENDER;

  const scope = useMemo<AppointmentScope>(() => {
    const isGroup = isListView
      ? pathname.startsWith(PROVIDER_PATHS.GROUP_APPOINTMENTS)
      : searchParams.get(SCOPE_QUERY_PARAM) === APPOINTMENT_SCOPE.GROUP;

    return isGroup ? APPOINTMENT_SCOPE.GROUP : APPOINTMENT_SCOPE.INDIVIDUAL;
  }, [isListView, pathname, searchParams]);

  // Keep the search so the selected view (filterDateType) carries over.
  const setScope = useCallback(
    (next: AppointmentScope) => {
      if (isListView) {
        navigate({ pathname: SCOPE_PATH[next], search });
        return;
      }

      if (next === APPOINTMENT_SCOPE.GROUP) {
        searchParams.set(SCOPE_QUERY_PARAM, next);
      } else {
        searchParams.delete(SCOPE_QUERY_PARAM);
      }
      setSearchParams(searchParams, { replace: true });
    },
    [isListView, navigate, search, searchParams, setSearchParams]
  );

  const showScope = useCallback(
    (next: AppointmentScope) => {
      if (isListView || isCalendarView) {
        setScope(next);
        return;
      }
      navigate(SCOPE_PATH[next]);
    },
    [isListView, isCalendarView, setScope, navigate]
  );

  const getViewPath = useCallback(
    (view: "calendar" | "list"): To => {
      if (view === "list") return SCOPE_PATH[scope];
      return scope === APPOINTMENT_SCOPE.GROUP
        ? {
            pathname: PROVIDER_PATHS.CALENDER,
            search: `?${SCOPE_QUERY_PARAM}=${scope}`,
          }
        : PROVIDER_PATHS.CALENDER;
    },
    [scope]
  );

  return { scope, setScope, showScope, getViewPath };
};
